<div class="mc-spring-board" *ngIf="{ state: pullState$ | async, terminalState: terminalState$ | async, filePaths: (filePaths$ | async) ?? [], rows: (rows$ | async) ?? [], stateExpired: (stateExpired$ | async) ?? false, showUserStatusColumn: (hasUserStatusColumn$ | async) ?? false } as ctx">
  <mat-toolbar class="mc-spring-board-header" color="secondary">
    Pull Changes
    <span class="mc-fill-remaining-flex-space"></span>
    <button type="button" mat-icon-button color="secondary-invert" tabindex="-1" (click)="closeDialog()">
      <mat-icon fontSet="project-titan" fontIcon="icon-close"> </mat-icon>
    </button>
  </mat-toolbar>

  <!-- Shared file-status table — semantic <table mat-table> with three columns (status / path /
       issue). Reused by both the during-pull view and the post-pull summary so the layout stays
       identical across phases. `mat-table` is preferable to a CSS grid here because the data is
       tabular and we want headers, sortable column widths via `.mat-column-*`, and standard
       row/cell semantics for accessibility. -->
  <ng-template #fileRows let-stateForErrors="stateForErrors" let-rows="rows" let-showUserStatusColumn="showUserStatusColumn">
    <table mat-table [dataSource]="rows" [trackBy]="trackByPath" class="mc-collab-pull-dialog-table">
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Status</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *ngIf="getOutcomeIcon(row.outcome) as icon; else maybeSpinner">
            <mat-icon class="mc-image-icon" [color]="icon.color" [ngClass]="icon.cssClass" fontSet="project-titan" [fontIcon]="icon.fontIcon" [matTooltip]="icon.tooltip"></mat-icon>
          </ng-container>
          <ng-template #maybeSpinner>
            <mat-progress-spinner *ngIf="isRunning(ctx.state) && !row.outcome; else gitStatusBadge" color="accent-vivid" [diameter]="16" mode="indeterminate"></mat-progress-spinner>
          </ng-template>
          <!-- Pre-pull (no outcome, no spinner): show the file's remote git-status badge (A / M
               / D) so the user can tell at a glance whether each row is an incoming addition,
               modification, or deletion. Without this, a row representing a deletion looks
               identical to one representing an edit. Once a pull starts, the spinner takes over;
               once it completes, the outcome icon does. -->
          <ng-template #gitStatusBadge>
            <mc-workspace-file-git-status [status]="row.remoteGitStatus"></mc-workspace-file-git-status>
          </ng-template>
        </td>
      </ng-container>

      <ng-container matColumnDef="path">
        <th mat-header-cell *matHeaderCellDef>File</th>
        <td mat-cell *matCellDef="let row">
          <mc-self-truncating-path [filePath]="row.filePath"></mc-self-truncating-path>
        </td>
      </ng-container>

      <ng-container matColumnDef="error">
        <th mat-header-cell *matHeaderCellDef>
          <ng-container *ngIf="hasErrors$ | async">Issue</ng-container>
        </th>
        <td mat-cell *matCellDef="let row" class="mc-text-warn">{{ errorMessageForPath(row.filePath, stateForErrors) }}</td>
      </ng-container>

      <ng-container matColumnDef="userStatus">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let row">
          <span *ngIf="showsInProgressMarker(row)" class="mc-dot mc-dot-small mc-bg-info-2" matTooltip="Moved to In Progress"></span>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns(showUserStatusColumn); sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns(showUserStatusColumn);"></tr>
    </table>
  </ng-template>

  <!-- Admin-only "state aged out" hero: bootstrap returned no record, so the grid's seed is the
       last anyone will know about this pull. We don't know if it completed or failed — Redis's
       grace TTL elapsed before the dialog opened. Render this instead of leaving the running
       layout (which would spin forever). Ranked ahead of the other branches so a stale seed
       can't get matched by the running/terminal branches below. -->
  <ng-container *ngIf="ctx.stateExpired && !ctx.terminalState">
    <div class="mc-spring-board-form-body mc-collab-pull-dialog-summary">
      <mat-icon fontSet="project-titan" fontIcon="icon-clock" class="mc-collab-pull-dialog-summary-icon mc-image-icon mc-image-icon-x-small mc-text-muted"></mat-icon>
      <p class="mc-collab-pull-dialog-summary-headline text-large">Pull no longer tracked</p>
      <p class="mc-text-muted mc-collab-pull-dialog-summary-subtext">This pull's record has expired from the live tracker. The outcome isn't available — refresh the grid to clear stale rows.</p>
    </div>
    <div class="mc-spring-board-form-footer">
      <div class="mc-form-buttons">
        <button type="button" mat-raised-button color="primary" (click)="closeDialog()">Close</button>
      </div>
    </div>
  </ng-container>

  <!-- Workspace-already-up-to-date hero: shown for terminal up-to-date pulls (0 unresolved AND
      0 resolved) and also for non-admin pre-pull states with zero rows, which can occur when
      live remote-change updates remove the final row while the dialog is open. -->
  <ng-container *ngIf="shouldShowWorkspaceUpToDateView(ctx.state, ctx.terminalState, ctx.rows)">
    <div class="mc-spring-board-form-body mc-d-flex mc-flex-column">
      <div class="mc-collab-pull-dialog-summary">
        <mat-icon fontSet="project-titan" fontIcon="icon-checkmark" class="mc-collab-pull-dialog-summary-icon mc-image-icon mc-image-icon-x-small mc-text-primary"></mat-icon>
        <p class="mc-collab-pull-dialog-summary-headline text-large">{{ isWorkspaceUpToDateTerminal(ctx.terminalState) ? 'Pull complete' : 'No files to pull' }}</p>
        <p class="mc-text-muted mc-collab-pull-dialog-summary-subtext">Your workspace is up to date.</p>
      </div>
    </div>
    <div class="mc-spring-board-form-footer">
      <div class="mc-form-buttons">
        <button type="button" mat-raised-button color="primary" (click)="onDoneClicked()">Done</button>
      </div>
    </div>
  </ng-container>

  <!-- Failure: centered error message, no table. -->
  <ng-container *ngIf="isFailedTerminal(ctx.terminalState)">
    <div class="mc-spring-board-form-body mc-collab-pull-dialog-summary">
      <mat-icon fontSet="project-titan" fontIcon="icon-warning-filled" class="mc-collab-pull-dialog-summary-icon mc-image-icon mc-image-icon-x-small mc-text-warn"></mat-icon>
      <p class="mc-collab-pull-dialog-summary-headline text-large">Pull failed</p>
      <p class="mc-text-muted mc-collab-pull-dialog-summary-subtext">{{ ctx.terminalState.error?.message || 'An unexpected error stopped the pull. Try again, and if it keeps happening let your admin know.' }}</p>
    </div>
    <div class="mc-spring-board-form-footer">
      <div class="mc-form-buttons">
        <button type="button" mat-raised-button color="primary" (click)="onDoneClicked()">Done</button>
      </div>
    </div>
  </ng-container>

  <!-- Shared table view: pre-pull / during-pull / clean-complete-terminal (with files) /
       partial-complete-terminal. The header's avatar + progress bar + caption are the SAME DOM
       elements across during-pull and any post-pull terminal — Angular updates the bound values
       (color, percent, text, classes) in place rather than tearing down one block and rebuilding
       another, which prevents the flash that used to occur when a pull transitioned from running
       to its post-pull summary. The pre-pull sub-branch sits alongside in the same wrapper. -->
  <!-- Shared running / pre-pull / partial-terminal layout. Renders for:
         - partial terminal (completed-with-files OR captured terminal that isn't workspace-up-to-date / failed)
         - running and pre-pull states
       `stateExpired` only suppresses this branch when we ALSO don't have a captured terminal —
       otherwise an expired pull whose terminal we already have (e.g. partial-complete from the
       grid's seed) would render nothing at all. -->
  <ng-container *ngIf="!shouldShowWorkspaceUpToDateView(ctx.state, ctx.terminalState, ctx.rows) && !isFailedTerminal(ctx.terminalState) && (ctx.terminalState || !ctx.stateExpired)">
    <div class="mc-spring-board-form-body mc-d-flex mc-flex-column">
      <div class="mc-collab-pull-dialog-table-header" [class.mc-collab-pull-dialog-table-header-pre-pull]="isPrePull(ctx.state, ctx.terminalState)">

        <!-- Pre-pull anchor: shown only when there's no observed pull (no terminal, not running).
             Centered download icon + headline + intro caption. Hidden in admin view — the admin
             dialog is observer-only and only opens against an in-flight or already-terminal pull. -->
        <ng-container *ngIf="isPrePull(ctx.state, ctx.terminalState) && !data.adminView">
          <div class="mc-collab-pull-dialog-pre-pull-anchor text-large">
            <mat-icon fontSet="project-titan" fontIcon="icon-download" class="mc-image-icon mc-text-primary"></mat-icon>
            <span>{{ ctx.rows.length | mcPluralize: 'file-numeric' }} ready to pull</span>
          </div>
          <p class="mc-text-muted mc-collab-pull-dialog-pre-pull-caption">
            External changes have been detected in the {{ ctx.rows.length === 1 ? 'file' : 'files' }} below. Click Pull to merge the changes into your workspace.
          </p>
        </ng-container>

        <!-- Active header: shown during a running pull AND at partial-complete-terminal. Same DOM
             both times — progress bar color/value and caption text/classes bind to helper methods
             that branch on state. Transitioning from running to terminal updates these bindings
             without recreating the avatar component, progress bar, or caption paragraph. The
             "In Progress" workflow notice is folded into the caption text itself when applicable
             (see `headerCaptionText`) so we don't need a separate `<p>` block whose appearance
             would shift the table down the first time it becomes applicable mid-pull. -->
        <ng-container *ngIf="!isPrePull(ctx.state, ctx.terminalState) || data.adminView">
          <div class="mc-collab-pull-dialog-pulling-user">
            <ng-container *ngIf="pullingUser$ | async as pullingUser">
              <span class="mc-text-muted">Started by&nbsp;</span>
              <mc-license-user-avatar [licenseUser]="pullingUser" [showOnlineStatus]="false" size="small" compact="text"></mc-license-user-avatar>
              <span class="mc-text-muted" *ngIf="startedAtRelative$ | async as relative">&nbsp;{{ relative }}</span>
              <span class="mc-text-muted" *ngIf="completedAtRelative$ | async as completedRelative">&nbsp;&middot;&nbsp;completed {{ completedRelative }}</span>
            </ng-container>
          </div>
          <div class="mc-collab-pull-dialog-progress-metrics mc-text-muted">
            <ng-container *ngIf="activeHeaderState(ctx.state, ctx.terminalState)?.status === 'completed'; else processedMetrics">
              {{ mergedFileCount(activeHeaderState(ctx.state, ctx.terminalState)) }} merged
              <ng-container *ngIf="failedFileCount(activeHeaderState(ctx.state, ctx.terminalState)) > 0">
                , {{ failedFileCount(activeHeaderState(ctx.state, ctx.terminalState)) }} failed
              </ng-container>
            </ng-container>
            <ng-template #processedMetrics>
              {{ completedFileCount(activeHeaderState(ctx.state, ctx.terminalState)) }} / {{ totalFileCount(activeHeaderState(ctx.state, ctx.terminalState)) }} files processed
            </ng-template>
          </div>
          <mat-progress-bar class="mc-progress-bar-large" mode="determinate" [color]="headerProgressBarColor(activeHeaderState(ctx.state, ctx.terminalState))" [value]="headerProgressBarValue(activeHeaderState(ctx.state, ctx.terminalState))">
          </mat-progress-bar>
          <p class="mc-collab-pull-dialog-pulling-progress-text" [ngClass]="headerCaptionCssClasses(activeHeaderState(ctx.state, ctx.terminalState))">
            {{ headerCaptionText(activeHeaderState(ctx.state, ctx.terminalState)) }}
          </p>
        </ng-container>
      </div>

      <div class="mc-spring-board-body mc-collab-pull-dialog-rows-container mc-border-normal mc-border-divider mc-border-radius-normal">
        <ng-container *ngTemplateOutlet="fileRows; context: { stateForErrors: stateForRowErrors(ctx.state, ctx.terminalState), rows: ctx.rows, showUserStatusColumn: ctx.showUserStatusColumn }"></ng-container>
      </div>
    </div>

    <div class="mc-spring-board-form-footer">
      <div class="mc-form-buttons">
        <ng-container *ngIf="data.adminView; else nonAdminFooter">
          <button type="button" mat-raised-button color="primary" (click)="closeDialog()">Close</button>
        </ng-container>
        <ng-template #nonAdminFooter>
          <ng-container *ngIf="ctx.terminalState; else preOrDuringFooter">
            <button type="button" mat-raised-button color="primary" (click)="onDoneClicked()">Done</button>
          </ng-container>
          <ng-template #preOrDuringFooter>
            <ng-container *ngIf="isRunning(ctx.state); else prePullButtons">
              <span class="mc-form-buttons-left mc-text-muted">You can close this dialog - the pull will keep running in the background.</span>
              <button type="button" mat-raised-button color="primary" (click)="closeDialog()">Close</button>
            </ng-container>
            <ng-template #prePullButtons>
              <button type="button" mat-stroked-button (click)="closeDialog()">Cancel</button>
              <button type="button" mat-raised-button color="primary" (click)="onPullClicked(ctx.filePaths)" [disabled]="ctx.filePaths.length === 0">Pull</button>
            </ng-template>
          </ng-template>
        </ng-template>
      </div>
    </div>
  </ng-container>
</div>